import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Search, RefreshCw, ExternalLink, ArrowLeft } from 'lucide-react';
import { useAppSelector } from '../store';
import { useActiveEvent } from '../contexts/EventContext';
import { API_BASE_URL, cn } from '../lib/utils';
import { usePermission } from '../components/shared/PermissionGuard';
import PrfAccessReasonModal, { storePrfAccessSession } from '../components/command/PrfAccessReasonModal';
import type { PatientReportForm } from '../types/prf';
import type { Incident } from '../types';
import { manualRecordsToListRows, type PatientListRow } from '../lib/manualPatients';

type StoredPRF = Partial<PatientReportForm> & {
  id: string;
  incidentId: string;
  eventId?: string;
  submittedAt?: string;
  createdBy?: string;
  data?: Partial<PatientReportForm>;
  accessCount?: number;
};

export default function PRFListPage() {
  const navigate = useNavigate();
  const { token, user } = useAppSelector((state) => state.auth);
  const { activeEventId } = useActiveEvent();
  const [prfs, setPrfs] = useState<StoredPRF[]>([]);
  const [paperRows, setPaperRows] = useState<PatientListRow[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [pendingPrfId, setPendingPrfId] = useState<string | null>(null);
  const [accessBusy, setAccessBusy] = useState(false);
  const [accessError, setAccessError] = useState<string | null>(null);

  const canView = usePermission('prfs:view');

  const getFormTypeLabel = (p: StoredPRF) => {
    const formType = p.data?.formType;
    if (formType === 'minor_injury' || p.id.startsWith('MIF-')) return 'Minor Injury';
    if (formType === 'paracetamol_screening') return 'Paracetamol';
    return 'PRF';
  };

  const getPatientName = (p: StoredPRF) => {
    const pd = p.data?.patientDetails || p.patientDetails || {};
    const name = `${pd.firstName || ''} ${pd.lastName || ''}`.trim();
    return name || '(unnamed)';
  };

  const fetchPrfs = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const prfUrl = activeEventId
        ? `${API_BASE_URL}/prfs?eventId=${activeEventId}`
        : `${API_BASE_URL}/prfs`;
      const incUrl = activeEventId
        ? `${API_BASE_URL}/incidents?eventId=${activeEventId}`
        : `${API_BASE_URL}/incidents`;
      const [prfRes, incRes] = await Promise.all([
        fetch(prfUrl, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(incUrl, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (prfRes.ok) {
        const data = await prfRes.json();
        setPrfs(data.prfs || []);
      }
      if (incRes.ok) {
        const data = await incRes.json();
        const incidents: Incident[] = data.incidents || [];
        setPaperRows(
          incidents.flatMap((inc) => manualRecordsToListRows(inc.id, inc.eventId, inc.manualPatients)),
        );
      }
    } catch (e) {
      console.error('Failed to fetch patient records', e);
    } finally {
      setLoading(false);
    }
  }, [token, activeEventId]);

  useEffect(() => {
    if (canView) fetchPrfs();
  }, [fetchPrfs, canView]);

  if (!canView) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
          <h2 className="text-lg font-semibold text-foreground mb-1">Access Denied</h2>
          <p className="text-sm text-muted-foreground">You do not have permission to view Patient Report Forms.</p>
        </div>
      </div>
    );
  }

  const digitalRows: PatientListRow[] = prfs.map((p) => ({
    id: p.id,
    incidentId: p.incidentId,
    eventId: p.eventId,
    submittedAt: p.submittedAt || '',
    submittedByName: p.createdBy || '—',
    formTypeLabel: getFormTypeLabel(p),
    patientLabel: getPatientName(p),
    isPaper: false,
  }));

  const allRows = [...digitalRows, ...paperRows].sort(
    (a, b) => new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime(),
  );

  const filtered = allRows.filter((row) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      row.id.toLowerCase().includes(q) ||
      row.incidentId.toLowerCase().includes(q) ||
      row.patientLabel.toLowerCase().includes(q) ||
      row.formTypeLabel.toLowerCase().includes(q)
    );
  });

  const formatDate = (iso?: string) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  };

  const requestOpenPrf = (prfId: string) => {
    setAccessError(null);
    setPendingPrfId(prfId);
  };

  const confirmOpenPrf = async (reason: string) => {
    if (!token || !pendingPrfId) return;
    setAccessBusy(true);
    setAccessError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/prfs/${encodeURIComponent(pendingPrfId)}/access`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ reason }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to record access');
      storePrfAccessSession(pendingPrfId, {
        id: data.access.id,
        reason: data.access.reason,
        accessedAt: data.access.accessedAt,
        username: data.access.username,
      });
      const openId = pendingPrfId;
      setPendingPrfId(null);
      navigate(`/prfs/${encodeURIComponent(openId)}`);
    } catch (e) {
      setAccessError(e instanceof Error ? e.message : 'Failed to record access');
    } finally {
      setAccessBusy(false);
    }
  };

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Header */}
      <div className="flex-shrink-0 bg-card border-b border-border px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/')}
              className="p-1.5 rounded-md hover:bg-accent transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <FileText className="w-5 h-5 text-primary" />
            <div>
              <h1 className="text-base font-bold text-foreground">Patient Records</h1>
              <p className="text-xs text-muted-foreground">{filtered.length} {filtered.length === 1 ? 'record' : 'records'}</p>
            </div>
          </div>
          <button
            onClick={fetchPrfs}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-accent hover:bg-accent/80 transition-colors text-xs font-medium"
            disabled={loading}
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
        <div className="mt-3 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by reference, incident ID, patient notes or form type..."
            className="w-full h-9 pl-9 pr-3 text-sm bg-background border border-border rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        {filtered.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <FileText className="w-12 h-12 mx-auto text-muted-foreground/50 mb-3" />
              <p className="text-sm text-muted-foreground">
                {allRows.length === 0 ? 'No patient records yet' : 'No records match your search'}
              </p>
            </div>
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-card border-b border-border">
              <tr className="text-left text-muted-foreground">
                <th className="px-4 py-2 font-semibold">Reference</th>
                <th className="px-4 py-2 font-semibold">Patient</th>
                <th className="px-4 py-2 font-semibold">Incident</th>
                <th className="px-4 py-2 font-semibold">Submitted</th>
                <th className="px-4 py-2 font-semibold">Type</th>
                <th className="px-4 py-2 font-semibold w-16"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.map((row) => (
                <tr
                  key={row.id}
                  onClick={() => {
                    if (!row.isPaper) requestOpenPrf(row.id);
                  }}
                  className={cn(
                    'transition-colors',
                    row.isPaper ? 'hover:bg-accent/30' : 'hover:bg-accent/50 cursor-pointer',
                  )}
                >
                  <td className="px-4 py-2.5 font-mono text-foreground">{row.id}</td>
                  <td className="px-4 py-2.5 text-foreground">{row.patientLabel}</td>
                  <td className="px-4 py-2.5 font-mono text-muted-foreground">{row.incidentId}</td>
                  <td className="px-4 py-2.5 text-muted-foreground">{formatDate(row.submittedAt)}</td>
                  <td className="px-4 py-2.5">
                    <span className={cn(
                      'px-2 py-0.5 rounded-full text-[10px] font-semibold',
                      row.isPaper ? 'bg-amber-500/20 text-amber-400' : 'bg-green-500/20 text-green-400',
                    )}>
                      {row.isPaper ? 'Paper' : 'Digital'} · {row.formTypeLabel}
                    </span>
                  </td>
                  <td className="px-4 py-2.5">
                    {!row.isPaper && <ExternalLink className="w-3.5 h-3.5 text-muted-foreground" />}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <PrfAccessReasonModal
        open={!!pendingPrfId}
        prfId={pendingPrfId || ''}
        username={user?.username || user?.email || '—'}
        busy={accessBusy}
        error={accessError}
        onCancel={() => {
          if (accessBusy) return;
          setPendingPrfId(null);
          setAccessError(null);
        }}
        onConfirm={confirmOpenPrf}
      />
    </div>
  );
}
